// app/javascript/projects/components/ProjectsDisplay.jsx

import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import ThreadContainer from './ThreadContainer'
import ExcerptBody from './ExcerptBody'
import ExcerptConstants from 'constants/ExcerptConstants'
import { EXPAND_MEMO_STEP } from 'memos/MemoConstants';
import CodeOperationsContext from 'contexts/CodeOperationsContext';
import { useExcerpt } from 'hooks/excerpts/queries';
import useTrackingPage from 'hooks/useTrackingPage';

const Excerpt = React.memo((props) => {
  const { onRemoveCode } = useContext(CodeOperationsContext);
  const page = useTrackingPage();
  const [hover, setHover] = useState(false);
  const [hoverExcerpt, setHoverExcerpt] = useState(false);
  const [memosExpanded, setMemosExpanded] = useState(false);
  const [expandedMemoList, setExpandedMemoList] = useState([]);
  const [memoCount, setMemoCount] = useState(0);

  // Fetch excerpt data using the ID
  const { data: excerpt } = useExcerpt(props.excerptId);

  const [everHadContent, setEverHadContent] = useState(false);

  // Track when excerpt gets codings OR memos
  useEffect(() => {
    if (excerpt && !everHadContent) {
      const hasCodings = excerpt?.codings && excerpt.codings.length > 0;
      const hasMemos = excerpt?.memos && excerpt.memos.length > 0;

      if (hasCodings || hasMemos) {
        setEverHadContent(true);
      }
    }
  }, [excerpt?.codings, excerpt?.memos, everHadContent]);


  const onMouseEnter = useCallback(() => {
    setHover(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setHover(false);
  }, []);

  const onMouseEnterExcerpt = useCallback(() => {
    setHoverExcerpt(true);
  }, []);

  const onMouseLeaveExcerpt = useCallback(() => {
    setHoverExcerpt(false);
  }, []);

  const memoExpanded = useCallback((id) => {
    setExpandedMemoList(prev => [...prev, id]);
  }, []);

  const increaseMemoCount = useCallback(() => {
    setMemoCount(prev => prev + EXPAND_MEMO_STEP);
    setMemosExpanded(true);
  }, []);

  const handleCodeRemove = useCallback((codeID) => {
    if (onRemoveCode) {
      // × on a code tag (attached to an excerpt or paragraph) — distinct
      // surface from the right-hand sidebar's code list.
      onRemoveCode(codeID, null, { page, surface: 'code_tag' });
    }
  }, [onRemoveCode, page]);

  const expandMemos = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();

    setMemoCount(prev => memosExpanded ? 0 : EXPAND_MEMO_STEP);
    setMemosExpanded(prev => !prev);
    setExpandedMemoList(prev => memosExpanded ? [] : prev);
  }, [memosExpanded]);

  const hasCodings = excerpt?.codings && (excerpt?.codings || []).length > 0;

  // Memoized calculations
  const hasMemos = useMemo(() => {
    return excerpt?.memos && (excerpt?.memos || []).length > 0;
  }, [excerpt?.memos]);

  const showMemoIndicator = useMemo(() => {
    return hasMemos || 
      (hoverExcerpt || props.selected) && props.hasEditPermission;
  }, [hasMemos, hoverExcerpt, props.selected, props.hasEditPermission]);

  const memoCountValue = useMemo(() => {
    return excerpt?.memos ? excerpt.memos.length : 0;
  }, [excerpt?.memos]);

  // Early return logic
  if (!excerpt) {
    return <div></div>;
  }
  
  // Only hide if it never had codings OR memos since loading
  if (!everHadContent && !hasCodings && !hasMemos) {
    return <div></div>;
  }
  
  return (
    <div
      onMouseEnter={onMouseEnterExcerpt}
      onMouseLeave={onMouseLeaveExcerpt}
    >
      <div
        className="excerpt"
        style={{
          border: ExcerptConstants.BORDER,
          margin: '30px 0px',
          boxShadow: ExcerptConstants.BOX_SHADOW,
          borderRadius: ExcerptConstants.BORDER_RADIUS,
        }}
      >
        <ExcerptBody
          codePage={!!props.codePage}
          showMemoIndicator={showMemoIndicator}
          selected={props.selected}
          hasEditPermission={props.hasEditPermission}
          excerpt={excerpt}
          projectID={props.projectID}
          onCodeClick={props.onCodeClick}
          disabled={props.disabled}
          memoCount={memoCountValue}
          expandMemos={expandMemos}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          hover={hover}
          onTextClicked={props.onTextClicked}
          onCodeRemove={handleCodeRemove}
          onLinkClick={props.onLinkClick}
        />
        <ThreadContainer
          excerpt_id={excerpt.id}
          memos={excerpt.memos}
          memosExpanded={memosExpanded}
          expandedMemoList={expandedMemoList}
          memoExpanded={memoExpanded}
          increaseMemoCount={increaseMemoCount}
          memoCount={memoCount}
          hasEditPermission={props.hasEditPermission}
        />
      </div>        
    </div>
  );
});

// The memo wraps an anonymous arrow, so without this React DevTools (and the
// render-budget tests) would report it as "Anonymous".
Excerpt.displayName = 'Excerpt';

export default Excerpt;